import axiosInstance from '../helpers/axios';

/** Attachments (files and links) on any record — `GET /api/ops/documents`. */
export type TOpsDocument = {
  id: string;
  recordId: string;
  recordObject: string;
  kind: 'file' | 'link';
  name: string;
  url: string | null;
  contentType: string | null;
  size: number | null;
  storage: 's3' | 'local' | null;
  uploadedBy: string;
  at: string;
};

export type TOpsDocumentList = {
  recordId: string;
  total: number;
  storage: 's3' | 'local';
  items: TOpsDocument[];
};

export const getOpsDocuments = async (recordId: string) => {
  const response = await axiosInstance.get<TOpsDocumentList>('ops/documents', {
    params: { recordId },
  });
  return response.data;
};

export const addOpsDocumentLink = async (body: {
  recordId: string;
  recordObject: string;
  name: string;
  url: string;
}) => {
  const response = await axiosInstance.post<TOpsDocument>(
    'ops/documents/link',
    body,
  );
  return response.data;
};

export const uploadOpsDocument = async (
  file: File,
  recordId: string,
  recordObject: string,
  onProgress?: (percent: number) => void,
) => {
  const form = new FormData();
  form.append('recordId', recordId);
  form.append('recordObject', recordObject);
  form.append('file', file, file.name);
  const response = await axiosInstance.post<TOpsDocument>(
    'ops/documents/upload',
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      },
    },
  );
  return response.data;
};

/**
 * Fetch a file for saving. Local storage streams through the API (a blob
 * we hand to the browser); S3 returns a short-lived signed URL to open.
 */
export const downloadOpsDocument = async (doc: TOpsDocument) => {
  if (doc.storage === 's3') {
    const response = await axiosInstance.get<{ url: string; name: string }>(
      `ops/documents/${doc.id}/download`,
      { params: { json: 1 } },
    );
    window.open(response.data.url, '_blank', 'noopener');
    return;
  }
  const response = await axiosInstance.get<Blob>(
    `ops/documents/${doc.id}/download`,
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = doc.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

export const deleteOpsDocument = async (id: string) => {
  const response = await axiosInstance.delete<{ id: string; deleted: boolean }>(
    `ops/documents/${id}`,
  );
  return response.data;
};
